var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { BAR_CHART_BAR_LAYOUT, BAR_CHART_SMALL_BAR_GROUP_THRESHOLD, CHART_COLOR_TOKEN_HEX, CHART_COLORS } from '@web/config';
import { normalizeTooltipLabelWhitespace, parseIsoCalendarDayOrDateTime, parseMmmDInFilterRange } from '@web/utility/ tooltip/tooltipDateLabel';
import { AnalyticPeriodsEnum } from '@workalizer/shared';
import { getInterval, getMonthPeriods, getWeekPeriods } from '@workalizer/shared/utils';
import { addDays, differenceInDays, differenceInWeeks, endOfDay, format, parseISO, startOfDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
export var getDayPeriods = function (dateFrom, dateTo) {
    var daysDifferent = differenceInDays(dateTo, dateFrom);
    var dataDayPeriods = Array.from({ length: daysDifferent + 1 }, function (_, i) {
        var dayDate = addDays(startOfDay(dateFrom), i);
        var dayDateTo = endOfDay(dayDate);
        return {
            dateFrom: dayDate,
            dateTo: dayDateTo,
            period: format(dayDate, 'EEE - MMM d - yyyy', { locale: enUS })
        };
    });
    return dataDayPeriods;
};
export var getPeriodsByInterval = function (dateFrom, dateTo) {
    if (!dateFrom || !dateTo)
        return [];
    var dateFromDate = dateFrom instanceof Date ? dateFrom : new Date(dateFrom);
    var dateToDate = dateTo instanceof Date ? dateTo : new Date(dateTo);
    var interval = getInterval(dateFromDate, dateToDate);
    if (interval === AnalyticPeriodsEnum.DAY ||
        interval === AnalyticPeriodsEnum.HOUR) {
        return getDayPeriods(dateFromDate, dateToDate);
    }
    if (interval === AnalyticPeriodsEnum.WEEK) {
        return getWeekPeriods(dateFromDate, dateToDate);
    }
    if (interval === AnalyticPeriodsEnum.MONTH) {
        return getMonthPeriods(dateFromDate, dateToDate);
    }
    return getWeekPeriods(dateFromDate, dateToDate);
};
export function groupByPeriod(data, dateFrom, dateTo) {
    var weeksDifferent = differenceInWeeks(dateTo, dateFrom);
    if (weeksDifferent < 3)
        return data;
    var dataWeekPeriods = getWeekPeriods(dateFrom, dateTo);
    var dataWithWeekPeriod = dataWeekPeriods.reduce(function (acc, d) {
        var dataInPeriod = data
            .filter(function (item) {
            var date = new Date(item.date);
            return date >= d.dateFrom && date < d.dateTo;
        })
            .map(function (item) { return item.items; })
            .flat();
        acc.push({
            date: d.period,
            items: dataInPeriod
        });
        return acc;
    }, []);
    return dataWithWeekPeriod;
}
export var buildChartDataFromGroupedResponse = function (data, valueFormatter) {
    if (valueFormatter === void 0) { valueFormatter = function (n) { return n; }; }
    if (!(data === null || data === void 0 ? void 0 : data.length)) {
        return { chartData: [], categories: [] };
    }
    var chartData = [];
    data.forEach(function (unit) {
        unit.activities.forEach(function (activity) {
            var _a;
            var existing = chartData.find(function (d) { return d.name === activity.date; });
            var value = valueFormatter(activity.count);
            if (existing) {
                existing[unit.title] = (Number(existing[unit.title]) || 0) + value;
            }
            else {
                chartData.push((_a = { name: activity.date }, _a[unit.title] = value, _a));
            }
        });
    });
    var categories = data.map(function (unit) { return unit.title; });
    return { chartData: chartData, categories: categories };
};
export var buildChartDataFromDurationGroupedResponse = function (data, valueFormatter) {
    if (valueFormatter === void 0) { valueFormatter = function (n) { return n; }; }
    if (!(data === null || data === void 0 ? void 0 : data.length)) {
        return { chartData: [], categories: [] };
    }
    var chartData = [];
    data.forEach(function (unit) {
        unit.activities.forEach(function (activity) {
            var _a;
            var existing = chartData.find(function (d) { return d.name === activity.date; });
            var value = valueFormatter(activity.duration);
            if (existing) {
                existing[unit.title] = (Number(existing[unit.title]) || 0) + value;
            }
            else {
                chartData.push((_a = { name: activity.date }, _a[unit.title] = value, _a));
            }
        });
    });
    var categories = data.map(function (unit) { return unit.title; });
    return { chartData: chartData, categories: categories };
};
export var getChatData = function (data, users) {
    if (!(data === null || data === void 0 ? void 0 : data.length)) {
        return { chartData: [], categories: [] };
    }
    var categories = [];
    var categoriesSeen = new Set();
    var chartData = data.map(function (item) {
        var reduced = item.users.reduce(function (acc, eventItem) {
            var _a, _b;
            var key = (_a = users.find(function (u) { return u.id === eventItem.userId; })) === null || _a === void 0 ? void 0 : _a.name;
            if (!key)
                return acc;
            if (!categoriesSeen.has(key)) {
                categoriesSeen.add(key);
                categories.push(key);
            }
            acc[key] = ((_b = acc[key]) !== null && _b !== void 0 ? _b : 0) + eventItem.eventsCount;
            return acc;
        }, {});
        return __assign({ name: item.date }, reduced);
    });
    return { chartData: chartData, categories: categories };
};
export var calculateMinBarHeight = function (maxValue, percentage, minValue) {
    if (percentage === void 0) { percentage = 0.025; }
    if (minValue === void 0) { minValue = 0.1; }
    return Math.max(maxValue * percentage, minValue);
};
export var applyMinBarHeight = function (value, minBarHeight) {
    return value > 0 && value < minBarHeight ? minBarHeight : value;
};
export var SECONDS_PER_HOUR = 3600;
export function formatCollaborativeTimeYAxisSeconds(seconds) {
    return String(Math.round(Math.max(0, Number(seconds)) / SECONDS_PER_HOUR));
}
export function shouldRotateBarChartLabels(barGroupCount, forceLabelRotation) {
    if (forceLabelRotation === void 0) { forceLabelRotation = false; }
    return (forceLabelRotation || barGroupCount >= BAR_CHART_SMALL_BAR_GROUP_THRESHOLD);
}
export function getBarChartBarLayout(barGroupCount, barsPerGroup) {
    var size = barGroupCount >= BAR_CHART_SMALL_BAR_GROUP_THRESHOLD ? 'small' : 'normal';
    return BAR_CHART_BAR_LAYOUT["".concat(barsPerGroup, "-").concat(size)];
}
function getBarGroupHalfWidth(barGroupCount, barsPerGroup) {
    var _a = getBarChartBarLayout(barGroupCount, barsPerGroup), barGap = _a.barGap, barSize = _a.barSize;
    var groupWidth = barsPerGroup * barSize + Math.max(0, barsPerGroup - 1) * barGap;
    return groupWidth / 2;
}
function getBarChartHorizontalEdgePadding(options) {
    return Math.ceil(getBarGroupHalfWidth(options.barGroupCount, options.barsPerGroup) + 4);
}
export function getPeriodChartXAxisProps(options) {
    var baseTick = { fontSize: 12, fill: '#6b7280' };
    var padding = {
        left: getBarChartHorizontalEdgePadding(options),
        right: getBarChartHorizontalEdgePadding(options)
    };
    if (shouldRotateBarChartLabels(options.barGroupCount, options.forceLabelRotation)) {
        return {
            interval: 0,
            tick: __assign(__assign({}, baseTick), { angle: -90, textAnchor: 'end' }),
            tickMargin: 8,
            padding: padding
        };
    }
    return {
        tick: baseTick,
        tickMargin: 16,
        padding: padding
    };
}
export function getPeriodChartBarChartMargin(options) {
    if (shouldRotateBarChartLabels(options.barGroupCount, options.forceLabelRotation)) {
        return { top: 5, right: 8, left: 8, bottom: 72 };
    }
    return { top: 8, right: 8, left: 4, bottom: 32 };
}
export function getPeriodBarChartLayoutProps(options) {
    var _a = getBarChartBarLayout(options.barGroupCount, options.barsPerGroup), barGap = _a.barGap, barSize = _a.barSize;
    return {
        barGap: barGap,
        barSize: barSize,
        periodXAxis: getPeriodChartXAxisProps(options),
        periodBarChartMargin: getPeriodChartBarChartMargin(options)
    };
}
export function formatDayPeriodChartAxisString(label, options) {
    var trimmed = normalizeTooltipLabelWhitespace(label).trim();
    if (!trimmed)
        return trimmed;
    var fromIso = parseIsoCalendarDayOrDateTime(trimmed);
    if (fromIso) {
        return format(fromIso, 'EEE - MMM d - yyyy', { locale: enUS });
    }
    if ((options === null || options === void 0 ? void 0 : options.referenceDateFrom) && (options === null || options === void 0 ? void 0 : options.referenceDateTo)) {
        var d = parseMmmDInFilterRange(trimmed, options.referenceDateFrom, options.referenceDateTo);
        if (d)
            return format(d, 'EEE - MMM d - yyyy', { locale: enUS });
    }
    return trimmed;
}
export function formatChartDayPeriodAxisLabel(value, interval, range) {
    var s = String(value);
    if (interval !== AnalyticPeriodsEnum.DAY)
        return s;
    return formatDayPeriodChartAxisString(s, range
        ? { referenceDateFrom: range.dateFrom, referenceDateTo: range.dateTo }
        : undefined);
}
export function applyDayPeriodAxisNames(rows, interval, range) {
    if (interval !== AnalyticPeriodsEnum.DAY)
        return rows;
    return rows.map(function (row) { return (__assign(__assign({}, row), { name: formatChartDayPeriodAxisLabel(row.name, interval, range) })); });
}
export function appsBarChartColorsHex(seriesCount) {
    return Array.from({ length: seriesCount }, function (_, i) {
        var _a;
        var token = CHART_COLORS[i % CHART_COLORS.length];
        return (_a = CHART_COLOR_TOKEN_HEX[token]) !== null && _a !== void 0 ? _a : '#94a3b8';
    });
}
export function chartFillFromTokenOrHex(tokenOrHex, fallback) {
    var _a;
    if (fallback === void 0) { fallback = '#3578C8'; }
    if (!tokenOrHex)
        return fallback;
    if (tokenOrHex.startsWith('#'))
        return tokenOrHex;
    return (_a = CHART_COLOR_TOKEN_HEX[tokenOrHex]) !== null && _a !== void 0 ? _a : fallback;
}
export var STACKED_BAR_CORNER_RADIUS = 4;
var r = STACKED_BAR_CORNER_RADIUS;
var STACKED_BAR_RADIUS_ALL = [r, r, r, r];
var STACKED_BAR_RADIUS_NONE = [0, 0, 0, 0];
var STACKED_BAR_RADIUS_BOTTOM = [0, 0, r, r];
var STACKED_BAR_RADIUS_TOP = [r, r, 0, 0];
export function isStackedBarSegmentVisible(value) {
    return Number(value) > 0;
}
export function getStackedBarRadius(segmentIndex, segmentCount, stackValues) {
    var _a;
    var visibleIndices = (_a = stackValues === null || stackValues === void 0 ? void 0 : stackValues.flatMap(function (value, index) {
        return isStackedBarSegmentVisible(value) ? [index] : [];
    })) !== null && _a !== void 0 ? _a : Array.from({ length: segmentCount }, function (_, index) { return index; });
    if (visibleIndices.length === 0) {
        return STACKED_BAR_RADIUS_NONE;
    }
    if (visibleIndices.length === 1) {
        return visibleIndices[0] === segmentIndex
            ? STACKED_BAR_RADIUS_ALL
            : STACKED_BAR_RADIUS_NONE;
    }
    var first = visibleIndices[0];
    var last = visibleIndices[visibleIndices.length - 1];
    if (segmentIndex === first) {
        return STACKED_BAR_RADIUS_BOTTOM;
    }
    if (segmentIndex === last) {
        return STACKED_BAR_RADIUS_TOP;
    }
    return STACKED_BAR_RADIUS_NONE;
}
export function getStackedBarRadiusFromEntry(entry, stackDataKeys, segmentIndex) {
    return getStackedBarRadius(segmentIndex, stackDataKeys.length, stackDataKeys.map(function (key) { var _a; return Number((_a = entry[key]) !== null && _a !== void 0 ? _a : 0); }));
}
export function parseChartAxisDate(dateStr, dateFromRef, dateToRef) {
    if (dateToRef === void 0) { dateToRef = dateFromRef; }
    var trimmed = normalizeTooltipLabelWhitespace(dateStr).trim();
    var fromIso = parseIsoCalendarDayOrDateTime(trimmed);
    if (fromIso)
        return fromIso;
    var fromMmmD = parseMmmDInFilterRange(trimmed, dateFromRef, dateToRef);
    if (fromMmmD)
        return fromMmmD;
    return parseISO(trimmed);
}
var BAR_CHART_ROW_META_KEYS = new Set([
    'period',
    'name',
    'previousPeriodLabel'
]);
export function isBarChartRowsEmpty(rows, options) {
    var _a;
    if (rows.length === 0) {
        return true;
    }
    var exclude = new Set(__spreadArray(__spreadArray([], Array.from(BAR_CHART_ROW_META_KEYS), true), ((_a = options === null || options === void 0 ? void 0 : options.excludeKeys) !== null && _a !== void 0 ? _a : []), true));
    return rows.every(function (row) {
        var numericValues = Object.entries(row).filter(function (_a) {
            var key = _a[0], value = _a[1];
            return !exclude.has(key) && typeof value === 'number';
        });
        if (numericValues.length === 0) {
            return true;
        }
        return numericValues.every(function (_a) {
            var value = _a[1];
            return value === 0;
        });
    });
}
export function isBarChartCategoriesEmpty(rows, categories) {
    if (rows.length === 0 || categories.length === 0) {
        return true;
    }
    return rows.every(function (row) {
        return categories.every(function (category) { var _a; return Number((_a = row[category]) !== null && _a !== void 0 ? _a : 0) === 0; });
    });
}
